import React from 'react';
import { useImpersonation } from '../../../contexts/ImpersonationContext';
import { useAppSelector } from '../../../store';
import CustomButton from '../../UI/Buttons/CustomButton';
import EditPencil from '../../UI/Icons/EditPencil';
import './ImpersonationBanner.scss';

/**
 * Format seconds into MM:SS format
 */
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * ImpersonationBanner Component
 * 
 * Displays a prominent banner when an admin is impersonating a candidate.
 * Shows:
 * - Warning that user is in impersonation mode
 * - Candidate name and email being impersonated
 * - Time remaining in the session
 * - End Session button
 */
const ImpersonationBanner: React.FC = () => {
  const { isImpersonating, impersonationData, endSession, remainingTime } =
    useImpersonation();
  const userpersonaldetails = useAppSelector((state) => state.onboarding.userpersonaldetails);

  if (!isImpersonating || !impersonationData) {
    return null;
  }

  const isExpiringSoon = remainingTime !== null && remainingTime <= 300; // 5 minutes

  const profileSteps = userpersonaldetails?.profileSteps?.[0];
  const accessText = profileSteps?.action === 'activate' && profileSteps?.step_number === 3 ? 'Impersonate Mode' : 'Accessing Onboarding';

  

  return (
    <div
      className={`impersonation-banner ${isExpiringSoon ? 'impersonation-banner--expiring' : ''}`}
    >
      <div className="impersonation-banner__content">
        <div className="impersonation-banner__info fs-16">
          <strong>{accessText || "Accessing Onboarding"} </strong>
          for{' '}{impersonationData.candidate.name || 'Candidate'}
          {' '}
          ({impersonationData.candidate.email})
        </div>
        <div className="impersonation-banner__divider d-none d-md-block" />
        <div className='fs-14'><EditPencil color="#FFF" size={20} /> Ability to view and edit as the candidate.</div>
        {remainingTime !== null && (
          <>
            <div className="impersonation-banner__divider  d-none d-md-block" />
            <div className="impersonation-banner__timer-container">
              <span>Session expires in:</span>
              <strong className="impersonation-banner__timer">
                {formatTime(remainingTime)}
              </strong>
            </div>
          </>
        )}
      </div>

      <CustomButton
        variant="primary"
        className="impersonation-banner__end-btn"
        onClick={endSession}
      >
        End Session
      </CustomButton>
    </div>
  );
};

export default ImpersonationBanner;
